import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import OptimizedImage from '../common/OptimizedImage';

function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const ticking = useRef(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    if (!ticking.current) {
      window.requestAnimationFrame(() => {
        const scrollPosition = window.scrollY || window.pageYOffset;
        setIsScrolled(scrollPosition > 50);
        ticking.current = false;
      });
      ticking.current = true;
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    const resetGlobalScrollLocks = () => {
      // Keep global navbar behavior scroll-safe on Android across all routes.
      document.documentElement.classList.remove('fancybox-enabled');
      document.body.classList.remove('compensate-for-scrollbar');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.overflow = '';
      document.body.style.overflowY = '';
      document.documentElement.style.height = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.overflowY = '';
    };

    resetGlobalScrollLocks();
    const t1 = setTimeout(resetGlobalScrollLocks, 150);
    const t2 = setTimeout(resetGlobalScrollLocks, 800);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [location.pathname]);

  return (
    <>
      <style>
        {`
          .tc-navbar-preview .content .navbar-brand .logo {
            height: 64px !important;
            max-height: 64px !important;
            width: auto !important;
            object-fit: contain !important;
          }
          .tc-navbar-preview .content .navbar-nav .nav-link:hover,
          .tc-navbar-preview .content .navbar-nav .nav-link.active {
            color: #73bf44 !important;
          }
          .tc-navbar-preview .navbar-toggler {
            border: 1px solid rgba(255, 255, 255, 0.3);
            padding: 8px 12px;
          }
          .tc-navbar-preview .navbar-toggler:focus {
            box-shadow: 0 0 0 0.2rem rgba(115, 191, 68, 0.25);
          }
          .tc-navbar-preview .navbar-toggler-icon {
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 30 30'%3e%3cpath stroke='rgba(255, 255, 255, 1)' stroke-linecap='round' stroke-miterlimit='10' stroke-width='2' d='M4 7h22M4 15h22M4 23h22'/%3e%3c/svg%3e");
          }
          @media screen and (max-width: 991px) {
            .tc-navbar-preview {
              padding: 15px 20px !important;
              background: #000 !important;
            }
            .tc-navbar-preview,
            .tc-navbar-preview * {
              touch-action: pan-y;
            }
            .tc-navbar-preview.scrolled {
              padding: 10px 20px !important;
            }
            .tc-navbar-preview .navbar-collapse {
              max-height: calc(100vh - 90px);
              overflow-y: auto;
              -webkit-overflow-scrolling: touch;
            }
            .tc-navbar-preview .content {
              padding: 0 !important;
            }
            .tc-navbar-preview .content .navbar-brand {
              max-width: 220px;
              margin-inline-end: 0;
            }
            .tc-navbar-preview .content .navbar-brand .logo {
              height: 52px !important;
              max-height: 52px !important;
            }
            .tc-navbar-preview .navbar-nav {
              margin: 20px 0;
              padding: 0;
            }
            .tc-navbar-preview .navbar-nav .nav-item {
              margin-bottom: 5px;
            }
            .tc-navbar-preview .navbar-nav .nav-link {
              font-size: 15px;
              margin: 0;
              padding: 12px 15px;
              border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            }
            .tc-navbar-preview .navbar-nav .nav-link:last-child {
              border-bottom: none;
            }
            .tc-navbar-preview .dropdown-menu {
              background: rgba(0, 0, 0, 0.8);
              border: 1px solid rgba(255, 255, 255, 0.1);
              margin-top: 5px;
              margin-left: 15px;
              border-radius: 8px;
            }
            .tc-navbar-preview .dropdown-menu .dropdown-item {
              padding: 12px 20px;
              font-size: 14px;
              border-bottom: 1px solid rgba(255, 255, 255, 0.05);
            }
            .tc-navbar-preview .dropdown-menu .dropdown-item:hover {
              background-color: rgba(115, 191, 68, 0.2);
              color: #73bf44;
            }
            .tc-navbar-preview .nav-side {
              margin-top: 20px;
              padding-top: 20px;
              border-top: 1px solid rgba(255, 255, 255, 0.1);
            }
            .tc-navbar-preview .nav-side .butn {
              width: 100%;
              text-align: center;
              padding: 12px 20px;
              font-size: 14px;
            }
          }
          @media screen and (max-width: 767px) {
            .tc-navbar-preview {
              padding: 12px 15px !important;
            }
            .tc-navbar-preview.scrolled {
              padding: 8px 15px !important;
            }
            .tc-navbar-preview .content .navbar-brand {
              max-width: 180px;
            }
            .tc-navbar-preview .content .navbar-brand .logo {
              height: 46px !important;
              max-height: 46px !important;
            }
            .tc-navbar-preview .navbar-nav .nav-link {
              font-size: 14px;
              padding: 10px 12px;
            }
            .tc-navbar-preview .dropdown-menu {
              margin-left: 10px;
            }
            .tc-navbar-preview .dropdown-menu .dropdown-item {
              padding: 10px 15px;
              font-size: 13px;
            }
          }
          @media screen and (max-width: 575px) {
            .tc-navbar-preview {
              padding: 10px 12px !important;
            }
            .tc-navbar-preview.scrolled {
              padding: 6px 12px !important;
            }
            .tc-navbar-preview .content .navbar-brand {
              max-width: 150px;
            }
            .tc-navbar-preview .content .navbar-brand .logo {
              height: 42px !important;
              max-height: 42px !important;
            }
            .tc-navbar-preview .navbar-toggler {
              padding: 6px 10px;
            }
            .tc-navbar-preview .navbar-nav .nav-link {
              font-size: 13px;
              padding: 8px 10px;
            }
            .tc-navbar-preview .nav-side .butn {
              padding: 10px 15px;
              font-size: 13px;
            }
          }
        `}
      </style>
      <nav className={`navbar navbar-expand-lg navbar-dark tc-navbar-preview ${isScrolled ? 'scrolled' : ''}`}>
      <div className="container-fluid content">
        <Link className="navbar-brand" to="/">
          <OptimizedImage
            src="/home_preview/assets/img/logo.png"
            alt="SEECA Logo"
            style={{ height: '64px', maxHeight: '64px', width: 'auto', objectFit: 'contain' }}
            className="logo"
            loading="eager"
          />
        </Link>
        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarSupportedContent"
          aria-controls="navbarSupportedContent"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>
        <div className="collapse navbar-collapse" id="navbarSupportedContent">
          <ul className="navbar-nav me-auto mb-2 mb-lg-0">
            <li className="nav-item">
              <Link className="nav-link" to="/" >
              
                HOME
              </Link>
            </li>
            <li className="nav-item dropdown">
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a
                className="nav-link dropdown-toggle"
                href="#"
                role="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                ABOUT
              </a>
              <ul className="dropdown-menu">
                <li>
                  <Link className="dropdown-item" to="/about">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link className="dropdown-item" to="/our-process">
                    Our Process
                  </Link>
                </li>
                <li>
                  <Link className="dropdown-item" to="/partners">
                    Our Partners
                  </Link>
                </li>
              </ul>
            </li>
            <li className="nav-item dropdown">
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a
                className="nav-link dropdown-toggle"
                href="#"
                role="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                OUR SERVICES
              </a>
              <ul className="dropdown-menu">
                <li>
                  <Link className="dropdown-item" to="/architecture">
                    Architecture
                  </Link>
                </li>
                <li>
                  <Link className="dropdown-item" to="/interior-design">
                    Interior Design
                  </Link>
                </li>
                <li>
                  <Link className="dropdown-item" to="/construction">
                    Construction
                  </Link>
                </li>
                <li>
                  <Link className="dropdown-item" to="/building-sustainability">
                    Building Sustainability
                  </Link>
                </li>
                <li>
                  <Link className="dropdown-item" to="/smart-building-technologies">
                    Smart Building Technologies
                  </Link>
                </li>
              </ul>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to="/portfolio">
                PORTFOLIO
              </Link>
            </li>
            <li className="nav-item dropdown">
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a
                className="nav-link dropdown-toggle"
                href="#"
                role="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                CAREER
              </a>
              <ul className="dropdown-menu">
                <li>
                  <Link className="dropdown-item" to="/career/internship">
                    Apply Internship
                  </Link>
                </li>
                <li>
                  <Link className="dropdown-item" to="/career/job">
                    Apply Job
                  </Link>
                </li>
              </ul>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to="/blog">
                BLOGS
              </Link>
            </li>
          </ul>
          <div className="nav-side">
            <a
              href="/contact"
              className="butn hover-bg-black text-capitalize  rounded-pill text-white"
              style={{ backgroundColor: '#73bf44' }}
            >
              <span> CONTACT US </span>
            </a>
          </div>
        </div>
      </div>
    </nav>
    </>
  );
}

export default Navbar;
